import React from 'react';
import { motion } from 'framer-motion';
import {
  FiMenu,
  FiRefreshCw,
  FiCheckCircle,
  FiAlertCircle,
  FiXCircle,
  FiFolder,
  FiInfo,
  FiMoon,
  FiSun,
  FiCloud
} from 'react-icons/fi';

const Header = ({
  onMenuToggle,
  sidebarOpen,
  onValidation,
  validationState,
  onOpenProject,
  onOpenDriveProject,
  hasProject,
  isFileSystemSupported,
  isDriveConfigured,
  projectSource,
  theme,
  onThemeToggle
}) => {
  const getValidationIcon = () => {
    if (validationState.isValidating) {
      return <FiRefreshCw className="animate-spin" />;
    }
    
    const errorCount = validationState.results.filter(r => r.status === 'invalid').length;
    const warningCount = validationState.results.filter(r => r.status === 'warning').length;
    
    if (errorCount > 0) {
      return <FiXCircle className="text-error-500" />;
    } else if (warningCount > 0) {
      return <FiAlertCircle className="text-warning-500" />;
    } else if (validationState.results.length > 0) {
      return <FiCheckCircle className="text-success-500" />;
    }
    
    return null;
  };

  const getValidationText = () => {
    if (validationState.isValidating) {
      return `Validating... ${validationState.progress}%`;
    }
    
    const total = validationState.results.length;
    const valid = validationState.results.filter(r => r.status === 'valid').length;
    const invalid = validationState.results.filter(r => r.status === 'invalid').length;
    const warning = validationState.results.filter(r => r.status === 'warning').length;
    
    if (total === 0) {
      return 'No validation results';
    }
    
    return `${valid} valid, ${warning} warnings, ${invalid} errors`;
  };

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="theme-navbar border-b px-6 py-4"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={onMenuToggle}
            className="theme-icon-button"
            aria-label={sidebarOpen ? 'Collapse navigation menu' : 'Expand navigation menu'}
          >
            <FiMenu className="w-5 h-5" />
          </motion.button>
          
          <div>
            <h1 className="text-2xl font-bold text-secondary-800">AE Data Manager</h1>
            <p className="text-sm text-secondary-500">
              Local-first AE inspection file management tool
              {hasProject && (
                <span className="ml-2 text-xs font-medium uppercase tracking-wide">
                  Source: {projectSource === 'google-drive' ? 'Google Drive' : 'Local Folder'}
                </span>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onThemeToggle}
            className="theme-icon-toggle"
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            aria-pressed={theme === 'dark'}
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            type="button"
          >
            {theme === 'dark' ? <FiSun className="w-5 h-5" /> : <FiMoon className="w-5 h-5" />}
            <span className="text-sm font-medium hidden sm:inline">
              {theme === 'dark' ? 'Light' : 'Dark'}
            </span>
          </motion.button>

          {isDriveConfigured && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onOpenDriveProject}
              className="btn-outline flex items-center space-x-2"
              style={{ minWidth: '200px' }}
              type="button"
              title="Sign in with Google and pick a Drive folder"
            >
              <FiCloud />
              <span>{projectSource === 'google-drive' ? 'Change Drive Folder' : 'Open Drive Folder'}</span>
            </motion.button>
          )}

          {/* Open Project Button or Warning - Show button only if File System API is supported */}
          {isFileSystemSupported ? (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onOpenProject}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                !hasProject || projectSource !== 'local'
                  ? 'btn-primary shadow-lg'
                  : 'btn-secondary'
              }`}
              style={{ minWidth: '200px' }}
            >
              <FiFolder />
              <span>{!hasProject || projectSource !== 'local' ? 'Open Inspection Folder' : 'Change Folder'}</span>
            </motion.button>
          ) : (
            <div className="theme-alert-error flex items-center space-x-2 px-4 py-2 rounded-lg" style={{ minWidth: '200px' }}>
              <FiInfo className="text-red-500" />
              <span className="text-sm font-medium">
                File System API Not Supported
              </span>
            </div>
          )}
          
          {/* Browser Compatibility Warning - Show when File System API is not supported */}
          {!isFileSystemSupported && (
            <div className="theme-alert-warning flex items-center space-x-2 px-4 py-2 rounded-lg">
              <FiAlertCircle />
              <span className="text-sm font-medium">
                {typeof navigator !== 'undefined' && navigator.userAgent.includes('Firefox') 
                  ? 'Firefox Detected: Limited Functionality' 
                  : 'Browser Not Supported: Limited Functionality'}
              </span>
            </div>
          )}

          {/* Validation Status */}
          {hasProject && (
            <div className="theme-subtle flex items-center space-x-2 px-4 py-2 rounded-lg">
              {getValidationIcon()}
              <span className="text-sm font-medium text-secondary-700">
                {getValidationText()}
              </span>
            </div>
          )}

          {/* Validation Button */}
          {hasProject && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onValidation}
              disabled={validationState.isValidating}
              className={`btn-primary flex items-center space-x-2 ${
                validationState.isValidating ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <FiRefreshCw className={validationState.isValidating ? 'animate-spin' : ''} />
              <span>Validate Files</span>
            </motion.button>
          )}
        </div>
      </div>

      {/* Validation Progress Bar */}
      {validationState.isValidating && (
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${validationState.progress}%` }}
          transition={{ duration: 0.3 }}
          className="theme-progress mt-4 h-1 rounded-full"
        />
      )}
    </motion.header>
  );
};

export default Header;
